import {
  AutocompleteCloseReason,
  ClickAwayListener,
  FormControl,
  FormControlLabel,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  flattenInfiniteFilterData,
  useGetDropdownAPI,
} from '@legacy/apis/dropdownAPI';
import { FilterTag } from '@legacy/components/layout/FilterLayout/components/MainContainer/components/FilterTag';
import { Autocomplete } from '@legacy/components/mui-components';
import { useMenu } from '@legacy/hooks/useMenu';
import { useGetCurrentPageIdentifier } from '@legacy/stores/PageStore/pageStore.zustand';
import { stringToPascal } from '@legacy/utils/string';
import { translationAnyText } from '@legacy/utils/translation';
import { useDebounce } from 'use-debounce';
import { filterOnKeyDown } from '../../../helpers';
import { ISearchFilter } from '../../../types';
import { getFilterIdFromContentUrl } from '../../../utils';
import styles from './FilterItem.module.scss';
import { IFilter } from './FilterItem.types';

interface IFilterItem extends ISearchFilter {
  'data-automation-id': string;
  onChange: (v: IFilter) => void;
  hideRadios?: boolean;
}

export const FilterItem = ({
  contentUrl,
  filterName,
  hideRadios,
  id,
  onChange,
  ...rest
}: IFilterItem) => {
  const { t } = useTranslation('assignFlow');

  const ref = useRef<HTMLDivElement>(null);
  const { anchorEl, menuIsOpen, menuOnClose, setAnchorEl } = useMenu();

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebounce(searchText, 500);

  const filterId = getFilterIdFromContentUrl(contentUrl);
  const pageIdentifier = useGetCurrentPageIdentifier();
  const take = 50;

  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetDropdownAPI({
      contentUrl,
      filterId,
      pageIdentifier,
      searchText: debouncedSearchText,
      take,
    });
  const { dropdownList, latestPageChildrenLength } =
    flattenInfiniteFilterData(data);

  const [isInclude, setIsInclude] = useState<'is' | 'is-not'>('is');
  const [filters, setFilters] = useState<IFilter['values']>([]);
  const shouldUpdateRef = useRef(false);

  const handleChange = useCallback(() => {
    if (shouldUpdateRef.current) {
      onChange({
        isInclude: isInclude === 'is',
        values: filters,
      });
      shouldUpdateRef.current = false;
    }
  }, [filters, isInclude, onChange]);

  useEffect(() => {
    handleChange();
  }, [handleChange]);

  const popperOnClose = () => {
    menuOnClose();
  };

  const filterTagOnRemove = () => {
    menuOnClose();
    onChange({
      isInclude: true,
      values: [],
    });
    setIsInclude('is');
    setFilters([]);
  };

  const radioOnChange = (_: ChangeEvent<HTMLInputElement>, v: string) => {
    if (v !== 'is' && v !== 'is-not') {
      return;
    }
    shouldUpdateRef.current = true;
    setIsInclude(v);
  };

  return (
    <FilterTag
      data-automation-id={rest['data-automation-id']}
      items={filters?.length ? filters : [{ label: t('FilterItems.Any') }]}
      onClick={(e) => setAnchorEl(e.currentTarget)}
      panelIsOpen={menuIsOpen}
      ref={ref}
      removeFilterTag={filters?.length ? filterTagOnRemove : undefined}
      title={translationAnyText(t, `FilterItems.${stringToPascal(filterName)}`)}
    >
      {menuIsOpen ? (
        <Popper
          anchorEl={anchorEl}
          className={styles.popper}
          data-automation-id={`${rest['data-automation-id']}-Popper`}
          disablePortal
          onKeyDown={(e) => filterOnKeyDown({ e, popperOnClose, ref })}
          open={menuIsOpen}
          placement="bottom-start"
        >
          <ClickAwayListener onClickAway={popperOnClose}>
            <Paper className={styles.paper} elevation={4} sx={{ width: 500 }}>
              <Stack gap={2}>
                {!hideRadios ? (
                  <FormControl>
                    <RadioGroup
                      className={styles.radioGroup}
                      name="IsOrIsNotRadio"
                      onChange={radioOnChange}
                      row
                      value={isInclude}
                    >
                      <FormControlLabel
                        aria-label={t('FilterItems.IsRadio')}
                        data-automation-id={`${rest['data-automation-id']}-IsRadio`}
                        control={<Radio />}
                        label={t('FilterItems.IsRadio')}
                        value="is"
                      />
                      <FormControlLabel
                        aria-label={t('FilterItems.IsNotRadio')}
                        data-automation-id={`${rest['data-automation-id']}-IsNotRadio`}
                        control={<Radio />}
                        label={t('FilterItems.IsNotRadio')}
                        value="is-not"
                      />
                    </RadioGroup>
                  </FormControl>
                ) : null}
                <Autocomplete
                  data-automation-id={`${rest['data-automation-id']}-Autocomplete`}
                  disableCloseOnSelect
                  disablePortal
                  filterSelectedOptions
                  getOptionDisabled={() => isFetching}
                  inputOnChange={setSearchText}
                  inputProps={{ autoFocus: true }}
                  inputValue={searchText}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  label={translationAnyText(
                    t,
                    `FilterItems.${stringToPascal(filterName)}`,
                  )}
                  loading={isFetching}
                  multiple
                  getOptionKey={(o) => (typeof o === 'string' ? o : o.value)}
                  noOptionsText={t('FilterItems.NoLabels')}
                  onChange={(_, newValue) => {
                    setSearchText('');
                    shouldUpdateRef.current = true;
                    setFilters(newValue as IFilter['values']);
                  }}
                  onClose={(_, reason: AutocompleteCloseReason) => {
                    if (reason === 'escape') {
                      popperOnClose();
                    }
                  }}
                  open
                  options={dropdownList}
                  placeholder={translationAnyText(
                    t,
                    `FilterItems.${stringToPascal(filterName)}`,
                  )}
                  size="small"
                  value={filters}
                  slots={{
                    popper: Popper,
                  }}
                  slotProps={{
                    listbox: {
                      onScroll: (event: SyntheticEvent) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 1; // Allow for a 1-pixel margin of error
                        if (
                          Math.abs(
                            listboxNode.scrollTop +
                              listboxNode.clientHeight -
                              listboxNode.scrollHeight,
                          ) > threshold
                        ) {
                          return;
                        }

                        if (
                          take === latestPageChildrenLength &&
                          hasNextPage &&
                          !isFetchingNextPage
                        )
                          fetchNextPage?.();
                      },
                    },
                  }}
                />
              </Stack>
            </Paper>
          </ClickAwayListener>
        </Popper>
      ) : null}
    </FilterTag>
  );
};
